/**
 * Resend API Client
 * Handles direct communication with Resend API
 */

import type { EmailOptions, SendEmailResponse } from '@/shared/types';

class ResendClient {
  private apiKey: string | undefined;

  constructor() {
    this.apiKey = import.meta.env.RESEND_API_KEY;
  }

  /**
   * Check if Resend is configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Send email via Resend API
   */
  async sendEmail(
    subject: string,
    html: string,
    options: EmailOptions
  ): Promise<SendEmailResponse> {
    if (!this.isConfigured()) {
      throw new Error('Resend API key is not configured');
    }

    try {
      const response = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: options.from || import.meta.env.VITE_RESEND_FROM_EMAIL || 'noreply@swaysuite.app',
          to: Array.isArray(options.to) ? options.to : [options.to],
          subject,
          html,
          tags: options.tags
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send email');
      }

      return await response.json();
    } catch (error) {
      console.error('Resend API error:', error);
      throw error;
    }
  }
}

export const resendClient = new ResendClient();
